import type { ReactNode } from "react"
import { Link as RouterLink, useLocation, useNavigate, type LinkProps as RouterLinkProps } from "react-router-dom"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { AuthProvider } from "@edemics/contexts/auth-provider"
import { TenantProvider } from "@edemics/contexts/tenant-provider"
import { PermissionProvider } from "@edemics/contexts/permission-provider"
import { ModuleProvider } from "@edemics/contexts/module-provider"
import { ChatProvider } from "@edemics/contexts/chat-context"
import { ThemeProvider } from "@edemics/components/theme-provider"
import { RouterAdapterProvider, type LinkProps } from "@edemics/components/router-adapter"
import { Toaster as SonnerToaster } from "@edemics/ui/sonner"
import { queryClient } from "./query-client"

function AdapterLink({ href, ...rest }: LinkProps) {
  const linkProps = { to: href, ...rest } as unknown as RouterLinkProps
  return <RouterLink {...linkProps} />
}

const adapter = {
  Link: AdapterLink,
  usePathname: () => useLocation().pathname,
}

/**
 * Every provider the tenant-app needs, in one place. Order matters:
 *
 *   1. QueryClientProvider — TanStack Query cache. Outermost so any
 *      provider (or child) can useQuery.
 *   2. TenantProvider       — resolves the subdomain → tenant. Runs FIRST
 *      after QueryClient so its fragment-token hydration (`#token=...`)
 *      populates sessionStorage before AuthProvider's own restore effect
 *      runs. Cross-host handoff (apex → subdomain) depends on this order.
 *   3. AuthProvider         — everyone below reads the current user.
 *   4. PermissionProvider   — reads user role to compute effective perms.
 *   5. ModuleProvider       — reads user role to determine module surface.
 *   6. ChatProvider         — reads user identity to open chat socket.
 *
 * Router injection: AuthProvider needs a `navigate` fn so login/logout can
 * push to /dashboard and /login without pulling next/navigation into
 * @edemics/contexts. React-router's useNavigate does the honours.
 */
export function Providers({ children }: { children: ReactNode }) {
  const navigate = useNavigate()

  return (
    <QueryClientProvider client={queryClient}>
      {/* ThemeProvider — wraps everything so `useTheme()` inside pages
          and the Header ThemeToggle actually flips the html class. Was
          missing pre-fix, which made every useTheme() call return the
          no-op default context and dark mode silently didn't work.
          storageKey matches the FOUC-prevention script in index.html so
          both agree on where the persisted theme lives. */}
      <ThemeProvider defaultTheme="system" storageKey="edemics-theme">
      <RouterAdapterProvider adapter={adapter}>
        <TenantProvider>
        <AuthProvider
          navigate={navigate}
          // PF-2 — tenant-app talks to the tenant-audience login endpoint.
          // Left as the AuthProvider default; passed explicitly here to
          // document the audience choice at the wire-up point.
          loginEndpoint="/auth/tenant-login"
          // Tenant-app Phase 1 — post-login lands on the single generic
          // /dashboard. The legacy Next.js flow was login → /dashboard/{role}
          // (trampoline that wrote localStorage.userRole then replaced to
          // /dashboard). Vite react-router doesn't need the trampoline:
          // AuthProvider already persists userRole on login, and the shared
          // Sidebar reads it (or the `role` prop) to pick the menu. So we
          // send everyone straight to /dashboard.
          postLoginPath={() => "/dashboard"}
        >
          <PermissionProvider>
            <ModuleProvider>
              <ChatProvider>
                {children}
                {import.meta.env.DEV ? <ReactQueryDevtools initialIsOpen={false} /> : null}
              </ChatProvider>
            </ModuleProvider>
          </PermissionProvider>
        </AuthProvider>
        </TenantProvider>
      </RouterAdapterProvider>
      {/* Phase W R.0 — mount sonner <Toaster> once at the app shell.
          Pre-fix: every toast.* call across the whole tenant-app succeeded
          as a function invocation but went to a null renderer because no
          Toaster was mounted. That silently killed inline validation
          messages, success confirmations and error toasts on ~20
          accounting pages (and anywhere else toast.* fires). */}
      <SonnerToaster richColors position="top-right" closeButton />
      </ThemeProvider>
    </QueryClientProvider>
  )
}
